/**
 * Asks for the password a protected PDF needs before it will open -- or,
 * when combining, before its pages can be taken.
 */

import { useEffect, useRef, useState } from 'react'

interface Props {
  filename: string
  /** The last attempt was the wrong password. */
  wrong: boolean
  onSubmit: (password: string) => void
  onCancel: () => void
  /** The button's label: "Open" unless this is for combining. */
  action?: string
  /** The file opens, but its restrictions need the permissions password. */
  permissionsOnly?: boolean
}

export function PasswordDialog({
  filename, wrong, onSubmit, onCancel, action = 'Open', permissionsOnly = false,
}: Props) {
  const [password, setPassword] = useState('')
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setPassword('')
    input.current?.focus()
  }, [wrong])

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="pw-title">
      <form
        className="modal__panel"
        onSubmit={(event) => {
          event.preventDefault()
          if (password) onSubmit(password)
        }}
      >
        <h2 className="modal__title" id="pw-title">Password required</h2>
        <div className="modal__body">
          {permissionsOnly ? (
            <p className="modal__hint">
              <strong>{filename}</strong> does not allow its pages to be taken
              into another document. Enter its permissions password to combine it.
            </p>
          ) : (
            <p className="modal__hint">
              <strong>{filename}</strong> is password protected. Enter its open
              password &mdash; or its permissions password, which also lets you
              change its protection.
            </p>
          )}
          <label className="field">
            <span>Password</span>
            <input
              ref={input}
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {wrong && <p className="modal__hint modal__hint--warn">That password is not right.</p>}
        </div>
        <div className="modal__footer">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!password}>{action}</button>
        </div>
      </form>
    </div>
  )
}
